import { PrimerosPasos } from "../components/PrimerosPasos";
import { wizardActivo } from "../lib/wizard";
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useConsumo } from "../contexts/ConsumoContext";
import PageHeader from "../components/PageHeader";
import { CheckCircle2, CreditCard, Loader2 } from "lucide-react";

import { cargarPlanes, conIva, precioDelPlan, rasgosDelPlan, useDatosAlta } from "../lib/planes";

export default function Onboarding() {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [checkoutAviso, setCheckoutAviso] = useState(null);
  const alta = useDatosAlta();
  const datosAlta = alta.datos;
  const { user, activeClinic, puede } = useAuth();
  const wizard = wizardActivo(user);
  const { consumo, refrescarConsumo } = useConsumo();
  const enPrueba = consumo?.estado === 'prueba' || consumo?.estado === 'prueba_terminada' || ['pendiente','activa','terminada'].includes(consumo?.prueba?.estado);

  const currentPlan = activeClinic?.plan_slug || "starter";
  const isDemo = activeClinic?.is_demo !== false;

  useEffect(() => {
    cargarPlanes()
      .then((data) => setPlans(Array.isArray(data) ? data : []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  const handleCheckout = async (plan_slug) => {
    setCheckoutLoading(plan_slug);
    setCheckoutError(null);
    setCheckoutAviso(null);
    try {
      const { data } = await api.post("/checkout-request", { plan_slug });
      // DT.268: un cambio de plan de una clinica que YA paga no pasa por Stripe Checkout: agency
      // cobra la diferencia en el acto y devuelve `upgraded`/`sin_cambios` con un `mensaje`
      // compuesto por el servidor. Antes se redirigia sin leer nada y el cliente no se enteraba
      // aqui de que ya se le habia cobrado.
      if (data.upgraded || data.sin_cambios) {
        setCheckoutAviso({ slug: plan_slug, msg: data.mensaje || "Tu plan quedó actualizado." });
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      const d = err?.response?.data;
      // El texto sale del backend (`{detail: str, code}`). Un rechazo de la tarjeta es un
      // RECHAZO, no una averia (COL.12): se pinta distinto, por `code` y nunca por el texto.
      const msg = typeof d?.detail === "string" ? d.detail : "No se pudo iniciar el pago. Intenta de nuevo.";
      setCheckoutError({ slug: plan_slug, msg, rechazo: d?.code === "UPGRADE_PAGO_RECHAZADO" });
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-6xl mx-auto w-full">
      <PageHeader title="Mi plan" />
      {wizard && <PrimerosPasos />}
      {/* ── Plan y Facturación ── */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden" data-testid="billing-card">
        <div className="px-5 py-3 border-b border-slate-200 font-display font-bold inline-flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-brand-electric" /> Plan y Facturación
        </div>
        <div className="p-5 space-y-5">
          {plansLoading || alta.estado === "cargando" ? (
            <div className="flex items-center justify-center py-10 gap-2 text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando planes…
            </div>
          ) : alta.estado === "error" && plans.length > 0 ? (
            /* TEN.4 (decisión de Jesús): sin el tope de usuarios del panel, la tarjeta omitiría un
               límite en silencio. Se avisa en vez de pintarla incompleta. */
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="billing-datos-error">
              No pudimos cargar los datos de los planes en este momento.{" "}
              <button onClick={alta.reintentar} className="underline font-medium">Reintentar</button>
            </div>
          ) : plans.length === 0 ? (
            /* Fallback si no se pudieron cargar planes */
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 p-4">
              <div className="space-y-1">
                <div className="text-sm font-semibold text-slate-900">
                  Plan actual: <span className="text-brand-electric capitalize">{currentPlan}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                  {isDemo ? "Periodo de prueba" : "Plan activo"}
                </div>
              </div>
              <button
                /* TEN.2 (18 Sep 2026): apuntaba a surit.ai/activar-clinica, que iba por el
                   checkout legacy de un solo pago y se retiro. Este boton vive en la rama
                   de FALLBACK: se pinta solo cuando la lista de planes no se pudo cargar,
                   asi que lo util aqui es volver a intentarlo -- la contratacion real esta
                   en esa lista, que ya va por el checkout unificado y recurrente. */
                onClick={() => { window.location.reload(); }}
                className="inline-flex items-center gap-2 bg-brand-electric text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 shadow-sm"
                data-testid="billing-activate-btn"
              >
                Reintentar
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {checkoutAviso && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="billing-aviso">
                  {checkoutAviso.msg}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {plans.map((plan) => (
                  <PlanCard
                    key={plan.slug}
                    plan={plan}
                    plans={plans}
                    colaboradores={datosAlta?.colaboradores_por_plan}
                    currentPlan={currentPlan}
                    enPrueba={enPrueba}
                    isDemo={isDemo}
                    onCheckout={handleCheckout}
                    checkoutLoading={checkoutLoading}
                    checkoutError={checkoutError}
                  />
                ))}
              </div>
            </div>
          )}

          {consumo && Object.prototype.hasOwnProperty.call(consumo, "cancelacion_programada") && puede?.("configuracion") && (
            <Suscripcion key={activeClinic?.id} consumo={consumo} refrescarConsumo={refrescarConsumo} contacto={datosAlta?.contacto_email} />
          )}


        </div>
      </div>

    </div>
  );
}

function fechaSuscripcion(valor) {
  if (!valor) return null;
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return null;
  return fecha.toLocaleDateString("es-MX", {
    day: "numeric", month: "long", year: "numeric", timeZone: "America/Mexico_City",
  });
}

function Suscripcion({ consumo, refrescarConsumo, contacto }) {
  const [plan, setPlan] = useState(consumo);
  const [confirmar, setConfirmar] = useState(false);
  const [pendiente, setPendiente] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { setPlan(consumo); }, [consumo]);
  const fecha = fechaSuscripcion(plan.vigente_hasta);
  const fin = fecha ? `el ${fecha}` : "el fin del periodo pagado";

  const cambiar = async (accion) => {
    setPendiente(true);
    setError(null);
    try {
      const { data } = await api.post(`/plan/${accion}`);
      setPlan(data);
      setConfirmar(false);
    } catch (err) {
      // Copy aprobado por Jesús (14 Oct 2026)
      setError(err?.response?.data?.code === "SIN_SUSCRIPCION_VIVA"
        ? "No encontramos una suscripción activa para cancelar."
        : `No pudimos procesar el cambio. Intenta de nuevo en unos minutos${contacto ? ` o escribe a ${contacto}` : ""}.`);
      setPendiente(false);
      return;
    }
    // La respuesta POST ya se pinta; un fallo al releer no convierte el éxito en error.
    try { await refrescarConsumo?.(); } catch { /* El contexto gestiona la falta de consumo. */ }
    setPendiente(false);
  };

  // Copy aprobado por Jesús (14 Oct 2026) (todos los textos de esta sección)
  return (
    <section className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="suscripcion">
      <h3 className="font-semibold text-slate-900">Cancelar suscripción</h3>
      {plan.cancelacion_programada ? (
        <>
          <p className="text-sm text-slate-600">Cancelación programada: tu plan termina {fin} y no se renovará.</p>
          <button disabled={pendiente} onClick={() => cambiar("reanudar")} className="px-4 py-2 rounded-lg border border-slate-300 text-sm disabled:opacity-60" data-testid="suscripcion-reanudar">Reanudar suscripción</button>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600">Si cancelas, tu plan sigue activo hasta {fin} y ya no se renueva. No se hacen más cargos.</p>
          {confirmar ? (
            <div className="space-y-3" data-testid="suscripcion-confirmacion">
              <p className="text-sm">¿Seguro que quieres cancelar? Conservas el acceso hasta {fin}.</p>
              <div className="flex flex-wrap gap-2">
                <button disabled={pendiente} onClick={() => cambiar("cancelar")} className="px-4 py-2 rounded-lg border border-rose-300 text-rose-700 text-sm disabled:opacity-60" data-testid="suscripcion-si">Sí, cancelar</button>
                <button disabled={pendiente} onClick={() => setConfirmar(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm disabled:opacity-60" data-testid="suscripcion-no">No, mantener mi plan</button>
              </div>
            </div>
          ) : (
            <button disabled={pendiente} onClick={() => { setError(null); setConfirmar(true); }} className="px-4 py-2 rounded-lg border border-rose-300 text-rose-700 text-sm disabled:opacity-60" data-testid="suscripcion-cancelar">Cancelar suscripción</button>
          )}
        </>
      )}
      {error && <p role="alert" className="text-sm text-rose-600">{error}</p>}
    </section>
  );
}

// TEN.4: el orden sale del catalogo (`sort_order`), no de un mapa de slugs escrito a mano que
// solo conocia los planes retirados. "Mas popular" se retiro: ninguna cifra lo respalda.
function PlanCard({ plan, plans, colaboradores, currentPlan, enPrueba, isDemo, onCheckout, checkoutLoading, checkoutError }) {
  const isCurrent = plan.slug === currentPlan;
  const actual = plans.find((p) => p.slug === currentPlan);
  const isDowngrade = actual ? (plan.sort_order ?? 0) < (actual.sort_order ?? 0) : false;
  const loading = checkoutLoading === plan.slug;
  const error = checkoutError?.slug === plan.slug ? checkoutError : null;

  return (
    <div className={`relative flex flex-col gap-4 rounded-2xl border-2 p-5 transition-shadow ${isCurrent ? "border-brand-electric bg-blue-50/30 shadow-md" : "border-slate-200"}`}>
      {isCurrent && (
        <span className="absolute top-3 right-3 text-[10px] bg-brand-electric text-white px-2 py-0.5 rounded-full font-semibold">
          Tu plan
        </span>
      )}

      <div>
        <div className="font-display font-bold text-lg text-slate-900">{plan.name}</div>
        <div className="mt-1 font-display text-2xl font-extrabold text-slate-900">
          {plan.requires_quote ? (
            // Sin precio fijo en el catálogo (price_monthly 0): antes pintaba «$0 MXN/mes».
            <span data-testid={`billing-precio-medida-${plan.slug}`}>A la medida</span>
          ) : (
            <>
              ${(plan.price_monthly || 0).toLocaleString("es-MX")}
              <span className="text-sm font-normal text-slate-500 ml-1">{conIva("MXN/mes", precioDelPlan(plan).modo)}</span>
            </>
          )}
        </div>
        {plan.setup_fee > 0 && (
          <div className="text-xs text-slate-500 mt-0.5">
            Implementación: ${(plan.setup_fee || 0).toLocaleString("es-MX")} único
          </div>
        )}
      </div>

      <ul className="space-y-1.5 flex-1">
        {rasgosDelPlan(plan, colaboradores).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
            <span className="text-slate-700">{f}</span>
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-1.5">
        {isCurrent && (!enPrueba || plan.requires_quote) ? (
          <button disabled className="w-full py-2 rounded-lg bg-slate-100 text-slate-500 text-sm font-medium cursor-not-allowed">
            Plan actual
          </button>
        ) : plan.requires_quote ? (
          <a
            href="https://wa.me/529995118476"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full block text-center py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
          >
            Hablar con ventas
          </a>
        ) : isDowngrade && !enPrueba ? (
          <button
            onClick={() => window.open("https://wa.me/529995118476?text=Hola, me gustaría cambiar mi plan de Surit Clínicas", "_blank")}
            className="w-full py-2 rounded-lg border border-slate-300 text-slate-600 text-sm font-medium hover:bg-slate-50"
          >
            ¿Quieres este plan?
          </button>
        ) : (
          <button
            onClick={() => onCheckout(plan.slug)}
            disabled={loading}
            className="w-full py-2 rounded-lg bg-brand-electric text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid={`billing-activate-btn-${plan.slug}`}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {enPrueba ? (isCurrent ? "Confirmar mi plan" : "Activar") : (isDemo ? "Activar" : "Cambiar a este plan")}
          </button>
        )}
        {error && (
          <p className={`text-xs text-center ${error.rechazo ? "text-amber-700" : "text-rose-600"}`} data-testid="billing-error">
            {error.msg}
          </p>
        )}
      </div>
    </div>
  );
}
